import "react-datepicker/dist/react-datepicker.css";
import { useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
  ValueFormatterParams,
  ValueFormatterFunc
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import useReference from "src/hooks/useReference";
import { MastercardFeeSummaryDetailModel } from "src/models/MastercardFeeSummaryDetailModel";


const MastercardFeeSummary = () => {
  const { setIsOpen } = useSidebar();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

  const [gridReady, setGridReady] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { currencyList } = useReference();

  const numericToAlpha = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (currencyList ?? []).forEach((c: any) => {
      if (c?.numericCode != null && c?.alphaCode) {
        map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
      }
    });
    return map;
  }, [currencyList]);

  const resolveAlphaFromRow = (data: MastercardFeeSummaryDetailModel): string => {
    const feeVal = data?.feeCurrencyCode ?? data?.transactionCurrencyCode;
    if (feeVal == null) return "USD";
    const str = String(feeVal).trim();
    if (/^[A-Za-z]{3}$/.test(str)) return str.toUpperCase();
    const mapped = numericToAlpha[String(str)];
    if (mapped) return mapped;
    return "USD";
  };

  const currencyFormatter: ValueFormatterFunc<MastercardFeeSummaryDetailModel, any> = (
    params: ValueFormatterParams<MastercardFeeSummaryDetailModel, any>
  ): string => {
    const v = params?.value;
    if (v === null || v === undefined) return "";
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n)) return "";
    const alphaCode = resolveAlphaFromRow(params.data as MastercardFeeSummaryDetailModel);
    return String(formatCurrency(n, alphaCode));
  };

  const [colDefs] = useState<ColDef<MastercardFeeSummaryDetailModel>[]>([
    { headerName: "ICA", field: "ica" },
    { headerName: "Reason Code", field: "reasonCode" },
    { headerName: "Service Type", field: "serviceType" },
    { headerName: "Fee Description", field: "feeDescription" },
    { headerName: "Reconciliation Amount", field: "feeAmount", valueFormatter: currencyFormatter },
    { headerName: "Transaction Amount", field: "transactionAmount", valueFormatter: currencyFormatter },
  ]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 120,
      defaultMaxWidth: 300,
      skipHeader: false,
    };
  }, []);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportMastercardFeeSummary(mapReportRequest(null))
      .then((res) => {
        downloadFile(MastercardFeeSummary.displayName!, res.data);
      })
      .finally(() => setIsLoading(false));
  };

  const createServerSideDatasource = (): IServerSideDatasource => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getMastercardFeeSummary(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        const success: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if (result?.rows?.length > 0) {
          params?.api?.hideOverlay();
          params?.success(success);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }

        // Add pinned bottom totals row if summary detail provided
        try {
          const sd: any = result?.summaryDetail ?? {};
          let totalFee: number | null = null;

          const ta = sd?.totalAmount;
          if (Array.isArray(ta)) {
            totalFee = ta.length > 0 ? (ta[0]?.totalFeeAmount ?? ta[0]?.TotalFeeAmount ?? null) : null;
          } else {
            totalFee = sd?.totalAmount?.totalFeeAmount ?? sd?.TotalAmount?.TotalFeeAmount ?? null;
          }

          if (totalFee != null) {
            // determine fee currency to allow existing currencyFormatter to work on the pinned row
            let feeCurrency: string | null = null;
            if (Array.isArray(ta)) {
              const raw = ta.length > 0 ? (ta[0]?.feeCurrencyCode ?? ta[0]?.transactionCurrencyCode ?? null) : null;
              feeCurrency = raw != null ? String(raw) : null;
            } else {
              const raw = sd?.totalAmount?.feeCurrencyCode ?? sd?.totalAmount?.transactionCurrencyCode ?? sd?.feeCurrencyCode ?? sd?.transactionCurrencyCode ?? null;
              feeCurrency = raw != null ? String(raw) : null;
            }
            // fallback to first data row currency so pinned row matches column formatting
            if ((feeCurrency === null || feeCurrency === undefined) && result?.rows?.length > 0) {
              const raw = result.rows[0]?.feeCurrencyCode ?? result.rows[0]?.transactionCurrencyCode ?? null;
              feeCurrency = raw != null ? String(raw) : null;
            }
            const totalsRow: Partial<MastercardFeeSummaryDetailModel> & { ica?: string } = {
              ica: "Totals",
              feeAmount: totalFee,
              feeCurrencyCode: feeCurrency ?? undefined,
              transactionCurrencyCode: feeCurrency ?? undefined,
            };

            ((params as any)?.api ?? (gridApi as any))?.setGridOption?.("pinnedBottomRowData", [totalsRow as any]);
          } else {
            ((params as any)?.api ?? (gridApi as any))?.setGridOption?.("pinnedBottomRowData", []);
          }
        } catch (e) {
          
        }
      })
      .catch(() => params?.fail?.());
  };

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Mastercard Fee Summary</h5>
        </div>
        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
            useIcaValue={true}
            showToDate={true}
          />
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

MastercardFeeSummary.displayName = "MastercardFeeSummary";

export default MastercardFeeSummary;
